/**
 * A URL the host made, marked so the renderer can tell.
 *
 * Decision 0017 has the host resolve a `morph-blob:` reference by reading the
 * bytes out of its own store and minting an object URL for them. That URL then
 * has to get past `mediaURL`, which refuses every scheme but `http(s)` because
 * *"a media value is data, and data is exactly where an untrusted URL would
 * arrive from."*
 *
 * The decision rejected the obvious fix — teaching the guard about `blob:` —
 * and was right to: a `blob:` URL is same-origin by construction, so it *looks*
 * safe, and the guard would then be trusting a string that arrived in data
 * rather than a value the host produced. What it did not say was how the host's
 * own URL gets through instead. This is that, and the amendment to 0017 says so.
 *
 * **A symbol is the difference.** Nothing that arrives through `projection` can
 * carry one: not JSON, not an IndexedDB record, not a network response. A value
 * marked here was marked by code holding this symbol, which is exactly the
 * property `brand.ts` uses to make the store's write doors unforgeable, and for
 * exactly the same reason.
 *
 * Not exported from the package index. A caller that cannot name the symbol
 * cannot mark a value with it — and the demo does not need to, because
 * `hostResolved` is the only thing it ever wants.
 */

const HOST_RESOLVED: unique symbol = Symbol('morph.runtime.host-resolved');

/** A media value whose `blob` is a URL this page minted. */
export interface HostResolvedMedia {
  readonly blob: string;
  readonly mime: string;
  readonly [HOST_RESOLVED]: true;
}

/** Mark a media value as one the host resolved. */
export const hostResolved = (blob: string, mime: string): HostResolvedMedia =>
  ({ blob, mime, [HOST_RESOLVED]: true });

/** Was this marked by something holding the symbol? */
export const isHostResolved = (v: unknown): v is HostResolvedMedia =>
  !!v && typeof v === 'object' && (v as HostResolvedMedia)[HOST_RESOLVED] === true;
